import React, { useState, useEffect, useRef } from 'react';
import { Search, Filter, Navigation, Users } from 'lucide-react';
import { Loader } from '@googlemaps/js-api-loader';

const MapView = ({ users, currentUser, events }) => {
  const [showFilters, setShowFilters] = useState(false);
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [userLocation, setUserLocation] = useState({ lat: 37.7749, lng: -122.4194 });

  useEffect(() => {
    // Get user's current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          console.log('Location access denied, using default location');
        }
      );
    }
  }, []);

  useEffect(() => {
    const initializeMap = async () => {
      const loader = new Loader({
        apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        version: 'weekly',
        libraries: ['places']
      });

      try {
        const google = await loader.load();
        
        const mapInstance = new google.maps.Map(mapRef.current, {
          center: userLocation,
          zoom: 14,
          styles: [
            {
              featureType: 'all',
              elementType: 'geometry.fill',
              stylers: [{ color: '#f5f5f5' }]
            },
            {
              featureType: 'water',
              elementType: 'all',
              stylers: [{ color: '#667eea' }, { saturation: 43 }, { lightness: -11 }]
            },
            {
              featureType: 'road',
              elementType: 'all',
              stylers: [{ hue: '#ff0000' }, { saturation: -100 }, { lightness: 45 }]
            },
            {
              featureType: 'poi',
              elementType: 'all',
              stylers: [{ visibility: 'simplified' }]
            }
          ],
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false
        });

        setMap(mapInstance);

        // Add current user marker
        new google.maps.Marker({
          position: userLocation,
          map: mapInstance,
          title: 'You',
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#667eea"/>
                <circle cx="12" cy="12" r="6" fill="white"/>
                <circle cx="12" cy="12" r="3" fill="#667eea"/>
              </svg>
            `),
            scaledSize: new google.maps.Size(24, 24)
          }
        });

        // Add user markers
        users.forEach((user, index) => {
          const marker = new google.maps.Marker({
            position: user.location,
            map: mapInstance,
            title: user.name,
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="16" r="12" fill="white" stroke="#667eea" stroke-width="2"/>
                  <circle cx="16" cy="16" r="8" fill="#667eea"/>
                </svg>
              `),
              scaledSize: new google.maps.Size(32, 32)
            }
          });

          // Add info window
          const infoWindow = new google.maps.InfoWindow({
            content: `
              <div style="padding: 8px; font-family: 'Montserrat', sans-serif;">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                  <img src="${user.avatar}" alt="${user.name}" 
                       style="width: 32px; height: 32px; border-radius: 50%; object-fit: cover;">
                  <div>
                    <div style="font-weight: 600; font-size: 14px;">${user.name}</div>
                    <div style="font-size: 12px; color: #666;">${user.status}</div>
                  </div>
                </div>
                <div style="font-size: 11px; color: #667eea; margin-bottom: 4px;">${user.distance} away</div>
                <div style="display: flex; gap: 4px; flex-wrap: wrap;">
                  ${user.skills.map(skill => `
                    <span style="background: rgba(102, 126, 234, 0.1); color: #667eea; 
                                 padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 500;">
                      ${skill}
                    </span>
                  `).join('')}
                </div>
              </div>
            `
          });

          marker.addListener('click', () => {
            infoWindow.open(mapInstance, marker);
          });
        });

        // Add event markers
        events.forEach(event => {
          new google.maps.Marker({
            position: { lat: 37.7849, lng: -122.4194 }, // Sample location for events
            map: mapInstance,
            title: event.title,
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="2" y="2" width="24" height="24" rx="6" fill="#f5576c" stroke="white" stroke-width="2"/>
                  <rect x="7" y="9" width="14" height="2" fill="white"/>
                  <rect x="7" y="13" width="10" height="2" fill="white"/>
                  <rect x="7" y="17" width="6" height="2" fill="white"/>
                </svg>
              `),
              scaledSize: new google.maps.Size(28, 28)
            }
          });
        });

      } catch (error) {
        console.error('Error loading Google Maps:', error);
      }
    };

    if (mapRef.current && !map) {
      initializeMap();
    }
  }, [userLocation, users, events, map]);

  const centerOnUser = () => {
    if (map && userLocation) {
      map.panTo(userLocation);
      map.setZoom(16);
    }
  };

  return (
    <div className="screen">
      <div className="map-container">
        <div 
          ref={mapRef} 
          style={{ width: '100%', height: '100%' }}
        />
        
        <div className="map-overlay">
          <input
            type="text"
            placeholder="Search locations, events, people..."
            className="search-bar"
          />
        </div>

        <div className="floating-controls">
          <button 
            className="floating-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={20} />
          </button>
          <button className="floating-btn" onClick={centerOnUser}>
            <Navigation size={20} />
          </button>
          <button className="floating-btn primary">
            <Users size={20} />
          </button>
        </div>
      </div>

      {/* Quick stats overlay */}
      <div style={{ 
        position: 'absolute', 
        bottom: '120px', 
        left: '20px', 
        right: '20px',
        display: 'flex',
        gap: '12px'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
          padding: '12px 16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          fontSize: '13px',
          fontWeight: '600',
          color: '#667eea',
          flex: 1,
          textAlign: 'center'
        }}>
          {users.length} Online
        </div>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
          padding: '12px 16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          fontSize: '13px',
          fontWeight: '600',
          color: '#667eea',
          flex: 1,
          textAlign: 'center'
        }}>
          {events.length} Events
        </div>
      </div>
    </div>
  );
};

export default MapView;